import { useState, FormEvent } from "react";
import { useTheme } from "@/hooks/useTheme";

export default function BookAuditSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const { isDark } = useTheme();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      await fetch("https://readdy.ai/api/form/d7eouq21i3t7k9116pg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const bg = isDark ? "#1C1A22" : "#FBF6EE";
  const headlineColor = isDark ? "white" : "#1C1A22";
  const mutedColor = isDark ? "rgba(255,255,255,0.38)" : "rgba(28,26,34,0.45)";
  const formCardBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.75)";
  const formCardBorder = isDark ? "rgba(255,255,255,0.07)" : "rgba(28,26,34,0.1)";
  const inputBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)";
  const inputBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(28,26,34,0.12)";
  const inputFocusBorder = "rgba(232,118,26,0.5)";
  const inputTextColor = isDark ? "white" : "#1C1A22";
  const inputPlaceholder = isDark ? "rgba(255,255,255,0.25)" : "rgba(28,26,34,0.3)";
  const selectBg = isDark ? "rgba(28,26,34,0.9)" : "rgba(255,255,255,0.9)";
  const dividerGradient = isDark
    ? "linear-gradient(90deg, transparent, rgba(232,118,26,0.35), rgba(245,200,66,0.25), transparent)"
    : "linear-gradient(90deg, transparent, rgba(232,118,26,0.45), rgba(245,200,66,0.35), transparent)";

  const benefitItems = [
    { icon: "ri-search-eye-line", title: "System Mapping", desc: "We document your entire current workflow from first contact to close.", accent: "#E8761A" },
    { icon: "ri-money-dollar-circle-line", title: "Revenue Leak Identification", desc: "We pinpoint exactly where opportunities are falling through the cracks.", accent: "#F5C842" },
    { icon: "ri-settings-4-line", title: "Integration Strategy", desc: "We design a custom system blueprint tailored to your operations.", accent: "#5A7BA8" },
    { icon: "ri-rocket-line", title: "Deployment & Optimization", desc: "We implement, test, and continuously optimize your connected system.", accent: "#E8761A" },
  ];

  return (
    <section id="book-audit" className="relative py-28 md:py-36 overflow-hidden transition-colors duration-500" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <div className="h-px w-full" style={{ background: dividerGradient }} />
      </div>

      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] pointer-events-none"
        style={{ background: isDark ? "radial-gradient(circle, rgba(232,118,26,0.05) 0%, transparent 65%)" : "radial-gradient(circle, rgba(232,118,26,0.06) 0%, transparent 65%)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-20">
          <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: "#E8761A" }}>Free Audit</p>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-thin leading-tight mb-6 tracking-tight transition-colors duration-500" style={{ color: headlineColor }}>
            Stop Losing Revenue to
            <br />
            <span className="font-semibold">Broken Processes.</span>
          </h2>
          <p className="text-base md:text-lg max-w-xl mx-auto font-light leading-relaxed transition-colors duration-500" style={{ color: mutedColor }}>
            Book your system audit. We&apos;ll map your workflow, identify revenue leaks, and design your integration strategy.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — Benefits */}
          <div className="space-y-8">
            {benefitItems.map((item, i) => (
              <div key={item.title} className="flex gap-5">
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: `${item.accent}12`, border: `1px solid ${item.accent}22` }}
                >
                  <i className={`${item.icon} text-base`} style={{ color: item.accent }}></i>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1.5">
                    <span className="text-[10px] font-mono" style={{ color: isDark ? "rgba(255,255,255,0.2)" : "rgba(28,26,34,0.25)" }}>0{i + 1}</span>
                    <h4 className="font-semibold text-sm transition-colors duration-500" style={{ color: headlineColor }}>{item.title}</h4>
                  </div>
                  <p className="text-sm leading-relaxed font-light transition-colors duration-500" style={{ color: mutedColor }}>{item.desc}</p>
                </div>
              </div>
            ))}

            <div className="relative rounded-2xl overflow-hidden mt-4">
              <img
                src="https://public.readdy.ai/ai/img_res/91b5bbd6a7385f8b8e9c0e43e877249e.jpg"
                alt="System Integration"
                className="w-full h-48 object-cover object-top"
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(to right, rgba(28,26,34,0.85) 0%, rgba(28,26,34,0.4) 50%, rgba(28,26,34,0.85) 100%)" }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-white text-lg font-light tracking-wide text-center px-6">
                  &ldquo;You stop managing tools.
                  <br />
                  <span className="font-semibold">You start running a system.&rdquo;</span>
                </p>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div
            className="rounded-3xl p-8 md:p-10 transition-all duration-500"
            style={{ background: formCardBg, border: `1px solid ${formCardBorder}` }}
          >
            {submitted ? (
              <div className="text-center py-14">
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-6"
                  style={{ background: "rgba(232,118,26,0.12)", border: "1px solid rgba(232,118,26,0.25)" }}
                >
                  <i className="ri-check-line text-2xl" style={{ color: "#E8761A" }}></i>
                </div>
                <h3 className="font-semibold text-xl mb-3 transition-colors duration-500" style={{ color: headlineColor }}>Audit Request Received</h3>
                <p className="text-sm font-light leading-relaxed transition-colors duration-500" style={{ color: mutedColor }}>
                  We&apos;ll be in touch within 24 hours to schedule your system audit.
                </p>
              </div>
            ) : (
              <form data-readdy-form id="book-audit-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="mb-2">
                  <h3 className="font-semibold text-xl mb-1 transition-colors duration-500" style={{ color: headlineColor }}>Book Your Free Audit</h3>
                  <p className="text-sm font-light transition-colors duration-500" style={{ color: mutedColor }}>No commitment. 30-minute strategy session.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest mb-2 transition-colors duration-500" style={{ color: mutedColor }}>Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="John Smith"
                      className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-all duration-300"
                      style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputTextColor }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = inputFocusBorder)}
                      onBlur={(e) => (e.currentTarget.style.borderColor = inputBorder)}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest mb-2 transition-colors duration-500" style={{ color: mutedColor }}>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 (555) 000-0000"
                      className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-all duration-300"
                      style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputTextColor }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = inputFocusBorder)}
                      onBlur={(e) => (e.currentTarget.style.borderColor = inputBorder)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest mb-2 transition-colors duration-500" style={{ color: mutedColor }}>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@company.com"
                    className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-all duration-300"
                    style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputTextColor }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = inputFocusBorder)}
                    onBlur={(e) => (e.currentTarget.style.borderColor = inputBorder)}
                  />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest mb-2 transition-colors duration-500" style={{ color: mutedColor }}>Industry</label>
                  <select
                    name="industry"
                    className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-all duration-300 cursor-pointer"
                    style={{ background: selectBg, border: `1px solid ${inputBorder}`, color: inputTextColor }}
                  >
                    <option value="">Select your industry</option>
                    <option value="Home Healthcare & Elder Care">Home Healthcare &amp; Elder Care</option>
                    <option value="Home Services">Home Services</option>
                    <option value="Golf Courses">Golf Courses</option>
                    <option value="Restaurants & Hospitality">Restaurants &amp; Hospitality</option>
                    <option value="Legal Services">Legal Services</option>
                    <option value="Financial Services">Financial Services</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest mb-2 transition-colors duration-500" style={{ color: mutedColor }}>
                    Biggest Challenge
                    <span className="ml-2 normal-case tracking-normal" style={{ color: isDark ? "rgba(255,255,255,0.2)" : "rgba(28,26,34,0.2)" }}>({charCount}/500)</span>
                  </label>
                  <textarea
                    name="biggest_challenge"
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your current workflow challenges..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-all duration-300 resize-none"
                    style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputTextColor }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = inputFocusBorder)}
                    onBlur={(e) => (e.currentTarget.style.borderColor = inputBorder)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-full text-white font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #E8761A, #F5C842)" }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.9")}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
                >
                  {submitting ? "Submitting..." : "Book Your System Audit Today"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
